/**
 * JobCodeDropdownColumnModule - Job Code Dropdown Column for UniversalTransactionsTable
 *
 * ARCHITECTURE:
 * - Single JobCodeCell component with readOnly gate to skip hooks
 * - Interactive path calls hooks once; read-only path calls zero hooks
 * - JobCodeSidesheetForm provides the same two display modes for the sidesheet
 */

import { useMemo } from 'react';
import { CodingSidesheetGrid } from 'app/pages/overview/tabs/transactions/CodingSidesheetGrid';
import JobCodesDropdown from 'app/pages/overview/tabs/transactions/JobCodesTransactions/JobCodesDropdown';
import { selectJobCodeSlice } from 'app/pages/store/jobCodeSlice';
import { HighnoteTransactionEvent } from 'API';
import { useAppSelector, useAppDispatch } from 'app/store';
import {
  selectTransactionById,
  updateTransaction,
  applyLocalTransactionUpdates,
} from 'app/pages/store/transactionsSlice';
import type { ColumnDef, CellContext } from '@tanstack/react-table';
import { noop, type CodingColumnId } from 'app/util/columnVisibility';
import type {
  CodingEntityRow,
  DropdownColumnModule,
  ModuleColumnProps,
  ToggleableColumn,
} from 'app/pages/overview/tabs/transactions/UniversalTransactionsTable';
import { JobCodeOption } from 'app/pages/overview/tabs/transactions/types';

export type { JobCodeOption };

// ---------------------------------------------------------------------------
// Table cell: read-only path (zero hooks)
// ---------------------------------------------------------------------------

function ReadOnlyJobCodeCell({ row }: CellContext<CodingEntityRow, unknown>) {
  const jc = row.original.jobCode;
  const selectedJobCode = jc ? { id: jc.id, label: `${jc.description} - ${jc.code}` } : null;

  return (
    <JobCodesDropdown
      selectedJobCode={selectedJobCode}
      options={selectedJobCode ? [selectedJobCode] : []}
      isDeprecated={false}
      onValueChange={noop}
    />
  );
}

// ---------------------------------------------------------------------------
// Table cell: interactive path (hooks called once)
// ---------------------------------------------------------------------------

function InteractiveJobCodeCell({ row }: CellContext<HighnoteTransactionEvent, unknown>) {
  const dispatch = useAppDispatch();
  const transaction = row.original;
  const freshTransaction =
    useAppSelector((state) => selectTransactionById(state, transaction.id)) || transaction;

  const { jobCodes } = useAppSelector(selectJobCodeSlice);

  const jobCodesOptions = useMemo(() => {
    return jobCodes?.map((jc) => ({
      id: jc?.id,
      label: `${jc?.description} - ${jc?.code}`,
      jobCodeImportId: jc?.jobCodeImportId,
    }));
  }, [jobCodes]);

  const selectedJobCode = useMemo(() => {
    const jcId = freshTransaction.jobCodeId;
    if (!jcId) return null;
    const jc = jobCodes?.find((j) => j?.id === jcId) ?? freshTransaction.jobCode;
    if (!jc) return null;
    return {
      id: jc.id,
      label: `${jc.description} - ${jc.code}`,
    };
  }, [freshTransaction.jobCodeId, freshTransaction.jobCode, jobCodes]);

  const updateTransactionJobCodeId = (jobCodeOption: JobCodeOption | null) => {
    const jobCodeId = jobCodeOption?.id ?? null;
    const jobCode = jobCodeId ? jobCodes?.find((j) => j?.id === jobCodeId) ?? null : null;
    dispatch(
      applyLocalTransactionUpdates([
        { id: freshTransaction.id, changes: { jobCodeId, jobCode: jobCode as any } },
      ])
    );
    dispatch(updateTransaction({ id: freshTransaction.id, jobCodeId }));
  };

  return (
    <JobCodesDropdown
      selectedJobCode={selectedJobCode}
      options={jobCodesOptions}
      isDeprecated={false}
      onValueChange={(jobCodeOption) => updateTransactionJobCodeId(jobCodeOption)}
    />
  );
}

// ---------------------------------------------------------------------------
// Unified table cell: gates on readOnly to skip hooks
// ---------------------------------------------------------------------------

function JobCodeCell(ctx: CellContext<HighnoteTransactionEvent | CodingEntityRow, unknown>) {
  const readOnly = (ctx.column.columnDef.meta as { readOnly?: boolean })?.readOnly;

  if (readOnly) {
    return <ReadOnlyJobCodeCell {...(ctx as CellContext<CodingEntityRow, unknown>)} />;
  }
  return <InteractiveJobCodeCell {...(ctx as CellContext<HighnoteTransactionEvent, unknown>)} />;
}

// ---------------------------------------------------------------------------
// Column map and module export
// ---------------------------------------------------------------------------

function getJobCodeDropdownColumns(props: ModuleColumnProps): ColumnDef<HighnoteTransactionEvent>[] {
  const { isVisible, getLabel, readOnly } = props;
  const columns: ColumnDef<HighnoteTransactionEvent>[] = [];

  if (isVisible('jobCode')) {
    columns.push({
      id: 'jobCode',
      accessorKey: 'jobCode',
      header: getLabel('jobCode'),
      cell: JobCodeCell as (
        props: CellContext<HighnoteTransactionEvent, unknown>
      ) => React.ReactElement | null,
      size: 190,
      enableSorting: false,
      enableHiding: true,
      meta: { readOnly },
    });
  }

  return columns;
}

function getJobCodeToggleableColumns(): ToggleableColumn[] {
  return [{ id: 'jobCode', label: 'Job Code', defaultVisible: true }];
}

export const jobCodeDropdownModule: DropdownColumnModule<HighnoteTransactionEvent> = {
  moduleId: 'jobCode',
  getColumns: getJobCodeDropdownColumns,
  getToggleableColumns: getJobCodeToggleableColumns,
  StatusCell: undefined,
};

// ---------------------------------------------------------------------------
// Sidesheet display configuration
// ---------------------------------------------------------------------------

export interface JobCodeSidesheetFormProps {
  transaction: CodingEntityRow | undefined;
  readOnly?: boolean;
  visibleColumnIds: CodingColumnId[];
  getLabel: (colId: CodingColumnId) => string;
  /** SEE-943: when provided, selection is reported here instead of writing
   *  through the transactions slice (used by the reimbursement sheet). */
  onJobCodeChange?: (jobCode: JobCodeOption | null) => void;
}

function InteractiveJobCodeSidesheetForm(props: JobCodeSidesheetFormProps) {
  const { transaction, visibleColumnIds, getLabel } = props;
  const dispatch = useAppDispatch();
  const hte = transaction as HighnoteTransactionEvent | undefined;
  const freshTransaction =
    useAppSelector((state) => (hte ? selectTransactionById(state, hte.id) : undefined)) || hte;

  const { jobCodes } = useAppSelector(selectJobCodeSlice);

  const jobCodesOptions = useMemo(() => {
    return jobCodes?.map((jc) => ({
      id: jc?.id,
      label: `${jc?.description} - ${jc?.code}`,
      jobCodeImportId: jc?.jobCodeImportId,
    }));
  }, [jobCodes]);

  const selectedJobCode = useMemo(() => {
    const jcId = freshTransaction?.jobCodeId;
    if (!jcId) return null;
    const jc = jobCodes?.find((j) => j?.id === jcId) ?? freshTransaction?.jobCode;
    if (!jc) return null;
    return { id: jc.id, label: `${jc.description} - ${jc.code}` };
  }, [freshTransaction?.jobCodeId, freshTransaction?.jobCode, jobCodes]);

  if (!transaction || !visibleColumnIds.includes('jobCode')) return null;

  const updateJobCode = (jobCodeOption: JobCodeOption | null) => {
    if (props.onJobCodeChange) {
      props.onJobCodeChange(jobCodeOption);
      return;
    }
    if (!freshTransaction?.id) return;
    const id = freshTransaction.id;
    const jobCodeId = jobCodeOption?.id ?? null;
    const jobCode = jobCodeId ? jobCodes?.find((j) => j?.id === jobCodeId) ?? null : null;
    dispatch(applyLocalTransactionUpdates([{ id, changes: { jobCodeId, jobCode: jobCode as any } }]));
    dispatch(updateTransaction({ id, jobCodeId }));
  };

  return (
    <CodingSidesheetGrid
      visibleColumnIds={['jobCode']}
      getLabel={getLabel}
      renderContent={() => (
        <JobCodesDropdown
          selectedJobCode={selectedJobCode}
          options={jobCodesOptions}
          isDeprecated={false}
          onValueChange={updateJobCode}
          fullWidth
        />
      )}
    />
  );
}

function ReadOnlyJobCodeSidesheetForm(props: JobCodeSidesheetFormProps) {
  const { transaction, visibleColumnIds, getLabel } = props;

  if (!transaction || !visibleColumnIds.includes('jobCode')) return null;

  const jc = transaction.jobCode;
  const selectedJobCode = jc ? { id: jc.id, label: `${jc.description} - ${jc.code}` } : null;

  return (
    <CodingSidesheetGrid
      visibleColumnIds={['jobCode']}
      getLabel={getLabel}
      renderContent={() => (
        <JobCodesDropdown
          selectedJobCode={selectedJobCode}
          options={selectedJobCode ? [selectedJobCode] : []}
          isDeprecated={false}
          onValueChange={noop}
          fullWidth
        />
      )}
    />
  );
}

export function JobCodeSidesheetForm(props: JobCodeSidesheetFormProps) {
  if (props.readOnly) return <ReadOnlyJobCodeSidesheetForm {...props} />;
  return <InteractiveJobCodeSidesheetForm {...props} />;
}

export default jobCodeDropdownModule;
